"use client"

import React, { useEffect, useMemo, useRef, useState } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { Send, Sparkles } from "lucide-react"
import { getApiBaseUrl } from "../../config/apiBase"
import "../../styles/components/RefinementGuide.css"

const openingMessage = {
  role: "assistant",
  content: "I’ll help turn what you are noticing into clear, practical suggestions. What should be clearer, safer, or easier to carry out?",
}

export default function RefinementGuide({
  itemType,
  itemId,
  itemTitle,
  currentItem,
  modules = [],
  fieldOptions = [],
  onSuggestionPosted,
  disabled = false,
}) {
  const { getAccessTokenSilently } = useAuth0()
  const [messages, setMessages] = useState([openingMessage])
  const [input, setInput] = useState("")
  const [drafts, setDrafts] = useState([])
  const [loading, setLoading] = useState(false)
  const [posting, setPosting] = useState(false)
  const [notice, setNotice] = useState("")
  const chatRef = useRef(null)
  const isAction = String(itemType).toLowerCase() === "action"

  const safeModuleType = useMemo(() => {
    if (!isAction) return ""
    return modules.includes(drafts[0]?.moduleType) ? drafts[0].moduleType : (modules[0] || "objective")
  }, [drafts, isAction, modules])

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight
  }, [messages, loading, drafts])

  const token = async () => getAccessTokenSilently({
    authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
  })

  const send = async () => {
    const content = input.trim()
    if (!content || loading) return
    const nextMessages = [...messages, { role: "user", content }]
    setMessages(nextMessages)
    setInput("")
    setLoading(true)
    setNotice("")
    try {
      const accessToken = await token()
      const response = await fetch(`${getApiBaseUrl()}/api/socraticguide/refinement-interview`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ messages: nextMessages, itemType, itemTitle, currentItem, modules, fieldOptions }),
      })
      if (!response.ok) throw new Error(`Guide request failed (${response.status})`)
      const data = await response.json()
      setMessages([...nextMessages, {
        role: "assistant",
        content: data.assistantMessage || "Tell me one more detail that would make this suggestion useful.",
      }])
      const nextDrafts = Array.isArray(data.draftSuggestions) && data.draftSuggestions.length
        ? data.draftSuggestions
        : data.draftSuggestion?.content ? [data.draftSuggestion] : []
      if (nextDrafts.length) setDrafts(nextDrafts.map((draft, index) => ({
        kind: draft.kind || draft.suggestionType || "detail",
        title: draft.title || (nextDrafts.length > 1 ? `Suggestion ${index + 1}` : "Suggested improvement"),
        field: draft.field || "Plan details",
        moduleType: draft.moduleType,
        content: draft.content,
        isBlocking: (draft.kind || draft.suggestionType) === "question" ? (draft.isBlocking ?? true) : false,
      })))
    } catch (error) {
      setMessages([...nextMessages, {
        role: "assistant",
        content: "I couldn’t shape the draft just now. Your words are still here—please try sending them again.",
      }])
    } finally {
      setLoading(false)
    }
  }

  const postDraft = async () => {
    if (!drafts.length || drafts.some(draft => !draft.content?.trim() || !draft.title?.trim()) || posting) return
    setPosting(true)
    setNotice("")
    try {
      const accessToken = await token()
      if (isAction) {
        const joinResponse = await fetch(`${getApiBaseUrl()}/api/actions/${itemId}/refinement/refiners`, {
          method: "POST",
          headers: { Authorization: `Bearer ${accessToken}` },
        })
        if (!joinResponse.ok) throw new Error(`Unable to join refinement (${joinResponse.status})`)
        const taskDrafts = drafts.filter(draft => draft.kind === "task")
        const planDrafts = drafts.filter(draft => draft.kind !== "task")
        if (taskDrafts.length) {
          const response = await fetch(`${getApiBaseUrl()}/api/Actions/${itemId}/tasks/suggestions`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
            body: JSON.stringify({ tasks: taskDrafts.map(draft => ({ title: draft.title, description: draft.content, priority: "medium" })) }),
          })
          if (!response.ok) throw new Error(`Task suggestions failed (${response.status})`)
        }
        for (const draft of planDrafts) {
          const moduleType = safeModuleType
          const response = await fetch(`${getApiBaseUrl()}/api/actions/${itemId}/refinement/modules/${moduleType}/suggestions`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
            body: JSON.stringify({ content: draft.content }),
          })
          if (!response.ok) throw new Error(`Suggestion request failed (${response.status})`)
        }
        setNotice(`${drafts.length} focused suggestion${drafts.length === 1 ? "" : "s"} posted. Other refiners can now discuss and vote.`)
        onSuggestionPosted?.(taskDrafts.length && !planDrafts.length ? "tasks" : safeModuleType)
      } else {
        for (const draft of drafts) {
          const response = await fetch(`${getApiBaseUrl()}/api/refinement/${itemType}/${itemId}`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
            body: JSON.stringify({ kind: draft.kind, title: draft.title, field: draft.field || "Plan details", content: draft.content, isBlocking: draft.kind === "question" ? draft.isBlocking !== false : false }),
          })
          if (!response.ok) throw new Error(`Suggestion request failed (${response.status})`)
        }
        setNotice(`${drafts.length} focused suggestion${drafts.length === 1 ? "" : "s"} posted. The group can now vote on each one.`)
        onSuggestionPosted?.()
      }
      setDrafts([])
    } catch (error) {
      setNotice("The suggestion could not be posted. Please try again.")
    } finally {
      setPosting(false)
    }
  }

  return (
    <section className="refinement-guide" aria-label="Refinement Guide">
      <div className="refinement-guide__heading">
        <span><Sparkles size={17} /> Guided refinement</span>
        <h2>Talk it through with the Refinement Guide</h2>
        <p>Describe what you would change in everyday language. The Guide will ask focused questions, separate bundled work into individual tasks, and prepare suggestions for you to review. Nothing is posted without your approval.</p>
      </div>

      {disabled && <p className="refinement-guide__notice" role="status">This refined plan has passed. Its approved version is locked, so the Guide is now read-only.</p>}

      <div className="refinement-guide__chat" ref={chatRef}>
        {messages.map((message, index) => (
          <div key={`${message.role}-${index}`} className={`refinement-guide__message is-${message.role}`}>
            <strong>{message.role === "assistant" ? "Refinement Guide" : "You"}</strong>
            <p>{message.content}</p>
          </div>
        ))}
        {loading && <div className="refinement-guide__thinking">Shaping the next question…</div>}
      </div>

      <div className="refinement-guide__composer">
        <textarea
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault()
              send()
            }
          }}
          placeholder="For example: We need a clearer volunteer plan and someone responsible for permits…"
          rows={3}
          disabled={disabled}
        />
        <button type="button" onClick={send} disabled={disabled || !input.trim() || loading}>
          <Send size={16} /> Send
        </button>
      </div>

      {drafts.length > 0 && (
        <div className="refinement-guide__draft">
          <span>{drafts.length} draft suggestion{drafts.length === 1 ? "" : "s"} · review each before posting</span>
          {drafts.map((draft, index) => (
            <article className="refinement-guide__draft-card" key={index}>
              <label>Suggestion type<select value={draft.kind} onChange={(event) => setDrafts(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, kind: event.target.value } : item))}><option value="task">Task</option>{!isAction && <option value="calendar_event">Calendar event</option>}<option value="field_edit">Plan edit</option><option value="detail">Added detail</option><option value="question">Open question</option></select></label>
              {draft.kind === "field_edit" && fieldOptions.length > 0 && <label>Part of the plan<select value={draft.field} onChange={(event) => setDrafts(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, field: event.target.value } : item))}>{fieldOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>}
              <label>Short title<input value={draft.title} onChange={(event) => setDrafts(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, title: event.target.value } : item))} /></label>
              <label>Suggestion<textarea value={draft.content} onChange={(event) => setDrafts(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, content: event.target.value } : item))} rows={5} /></label>
              {draft.kind === "question" && <label className="refinement-guide__blocking"><input type="checkbox" checked={draft.isBlocking !== false} onChange={(event) => setDrafts(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, isBlocking: event.target.checked } : item))} /> This answer is required before the complete plan can pass</label>}
              {drafts.length > 1 && <button type="button" className="is-remove" onClick={() => setDrafts(current => current.filter((_, itemIndex) => itemIndex !== index))}>Remove this draft</button>}
            </article>
          ))}
          <div>
            <button type="button" className="is-secondary" onClick={() => setDrafts([])}>Keep talking</button>
            <button type="button" onClick={postDraft} disabled={disabled || posting || drafts.some(draft => !draft.content?.trim() || !draft.title?.trim())}>
              {posting ? "Posting…" : isAction ? `Join refinement and post${drafts.length > 1 ? ` ${drafts.length}` : ""}` : `Post ${drafts.length === 1 ? "this suggestion" : `${drafts.length} suggestions`}`}
            </button>
          </div>
        </div>
      )}
      {notice && <p className="refinement-guide__notice" role="status">{notice}</p>}
    </section>
  )
}
